// @atlas/contracts — a `billing-profiles` modul Zod-sémái
//
// A fájl CSAK sémákat tart (érték-szintű Zod-objektumok + a belőlük inferált típusok) — a ROUTE-ok
// egyetlen `atlasContract` objektum-literálban maradnak (`src/index.ts`), mert a ts-rest
// kontextus-inferenciája a lapos bővítésre érzékeny. A sémák mozgatása ezt NEM érinti: sima értékek.
import { z } from 'zod';
import { isoDateStr, nullableStr } from '../_shared/schemas.js';
/**
 * ÜGYVEZETŐ-TÖRTÉNET egy bejegyzése. A név kötelező, az IDŐSZAK MINDKÉT VÉGE opcionális (user-döntés): a
 * legacyből örökölt nevekhez nincs dátum, és a gyakorlatban sem tudni mindig a pontos kezdetet. Nyitott
 * `endsOn` = „ma is ő".
 *
 * ÁTFEDÉST a szerződés SZÁNDÉKOSAN nem tilt (user-döntés): a hiányos valóságot rögzíteni kell tudni, az
 * átfedő/egyszerre nyitott bejegyzéseket a FELÜLET jelzi. Amit viszont tilt: a fordított időszak
 * (`endsOn` < `startsOn`) — az adat-bevitel elírása, nem valóság.
 */
export const ManagingDirectorSchema = z
    .object({
    name: z.string().trim().min(1).max(255),
    startsOn: isoDateStr.nullable().default(null),
    endsOn: isoDateStr.nullable().default(null),
})
    .refine((d) => d.startsOn === null || d.endsOn === null || d.endsOn >= d.startsOn, {
    // ISO-dátumokon a string-összehasonlítás kronologikus — nincs szükség Date-példányra.
    path: ['endsOn'],
    message: 'A záró dátum nem lehet korábbi a kezdőnél.',
});
/** A történet felső korlátja — egy profilnak a valóságban néhány ügyvezetője van; a plafon a visszaélés/elírás ellen. */
export const MANAGING_DIRECTORS_MAX = 50;
/** Billing-profil write-body (create + update közös törzs). A `billingName`/`zipCode`/`city`/`address`
 *  kötelező; a `billingEmails` minden eleme e-mail-formátum (üres tömb megengedett); a határidő 1–31 int. */
export const BillingProfileWriteBodySchema = z.object({
    billingName: z.string().trim().min(1).max(255),
    taxNumber: nullableStr(50),
    euTaxNumber: nullableStr(50),
    countryCode: z.string().trim().length(2).default('HU'),
    zipCode: z.string().trim().min(1).max(20),
    city: z.string().trim().min(1).max(255),
    address: z.string().trim().min(1).max(500),
    /** Ügyvezető-történet (üres tömb = nincs rögzítve). A régi, egymezős `managingDirector` megszűnt. */
    managingDirectors: z.array(ManagingDirectorSchema).max(MANAGING_DIRECTORS_MAX).default([]),
    billingEmails: z.array(z.string().email().max(255)).max(20).default([]),
    defaultInvoicePaymentDeadlineDays: z
        .preprocess((v) => (v === '' || v === null || v === undefined ? null : v), z.number().int().min(1).max(31).nullable())
        .optional(),
    /**
     * A profil alapértelmezett számla-megjegyzése — EGY mező (≤5000). A bizonylat egyetlen megjegyzés-blokkot
     * nyomtat, ezért a fejléc/lábléc kettősség megszűnt: a generálás és a kézi számla is ezt az egy értéket
     * viszi a számla `note` mezőjébe (a felhasználó ott felülírhatja).
     */
    defaultInvoiceNote: nullableStr(5000),
    /** Elsődlegessé tétel a create/update során (opcionális). Az ELSŐ profil auto-primary a BFF-ben,
     *  kivéve ha a kliens explicit `false`-t küld. A dedikált váltás a POST …/set-primary. */
    isPrimary: z.boolean().optional(),
});
/** Create-body: + cél-ügyfél (CSAK create-nél — update/delete a profil-sor tenantjából dolgozik). */
export const BillingProfileCreateBodySchema = BillingProfileWriteBodySchema.extend({
    clientId: z.string().uuid(),
});
/** Egy számlázási profil sora (lista + detail közös alak). A `clientName` a foundation `/v1/admin/clients`
 *  feloldásból (id → név); a pénz-mezők itt nincsenek (a profil nem tart összeget). */
export const BillingProfileRowSchema = z.object({
    id: z.string(),
    clientId: z.string(),
    /** A tulajdonos ügyfél neve (feloldva) — a lista-soron látszik. */
    clientName: z.string().nullable(),
    isPrimary: z.boolean(),
    billingName: z.string().nullable(),
    taxNumber: z.string().nullable(),
    euTaxNumber: z.string().nullable(),
    countryCode: z.string().nullable(),
    zipCode: z.string().nullable(),
    city: z.string().nullable(),
    address: z.string().nullable(),
    managingDirectors: z.array(z.object({
        name: z.string(),
        startsOn: z.string().nullable(),
        endsOn: z.string().nullable(),
    })),
    billingEmails: z.array(z.string()),
    defaultInvoicePaymentDeadlineDays: z.number().nullable(),
    defaultInvoiceNote: z.string().nullable(),
    /** OCC-verzió (a busz-sor `version`-je) — a szerkesztés/törlés/set-primary If-Match forrása. */
    version: z.number(),
    createdAt: z.string().nullable(),
    updatedAt: z.string().nullable(),
});
/** Billing-profil lista-lekérdezés: szabad-szöveg (név/adószám/város/cím/ügyvezető) + ügyfél-szűrő +
 *  csak-elsődleges + szerver-rendezés (whitelist) + offset-lapozás (a BFF-szűrt halmazon). */
export const BillingProfileListQuerySchema = z.object({
    q: z.string().optional(),
    clientId: z.string().uuid().optional(),
    onlyPrimary: z.coerce.boolean().optional(),
    sort: z.string().optional(),
    order: z.enum(['asc', 'desc']).optional(),
    limit: z.coerce.number().int().positive().max(200).optional(),
    offset: z.coerce.number().int().nonnegative().optional(),
});
/** A create-form ügyfél-választójának refs-adata (foundation `/v1/admin/clients`, id + név). */
export const BillingProfileClientRefsSchema = z.object({
    clients: z.array(z.object({ id: z.string(), name: z.string() })),
});
//# sourceMappingURL=schemas.js.map